import { connect } from 'react-redux';

import { openCreateTabUI, openCreatePageUI } from '../create/actions';
import { launchRemoveDialog } from '../remove/actions';
import {
  reOrderPages,
  submitAppcuesForms,
  checkAppcuesVisibleStatus,
  submitAppcuesReferFriend,
  setActiveAccountTab,
} from './actions';
import { launchDuplicatePageDialog } from '../create/duplicatePage/actions';

import {
  addIsPublishedListener,
  removeIsPublishedListener,
} from '../../../../../../app/modules/toolbar/actions';

import { openNewAdminDialog } from '../../../../../memberLogin/modules/admins/newAdmin/actions';
import { toggleAccountModal } from '../../../../../../modules/account/actions';
import { toggleUserMenu } from '../../../../../../modules/editor/internals/accountsDropdown/actions';

import { routeHasUnavailableComponent as _routeHasUnavailableComponent } from '../../../../../../utils';

import BuildMenu from './BuildMenu';
import { getAppAdmins } from '../admins/actions';

// Sidebar only needs component type (for plan checks / WordPress reorder guard),
// not the full alwaysData payload (text, images, etc.).
const mapComponentMeta = alwaysData => componentKey => {
  const component = alwaysData[componentKey];
  return {
    key: componentKey,
    type: component && component.type,
  };
};

const mapSidebarRoute = (
  key,
  alwaysData,
  routeHasUnavailableComponent,
  addons,
  specialAddonWithoutSubscription,
  isAppsumoUser,
) => {
  const data = alwaysData[key] || {};
  const mapMeta = mapComponentMeta(alwaysData);
  const tabs = data.tabs || [];
  const isTabbed = data.type === 'TabView' || data.type === 'NestedTabPage';

  const components = Array.isArray(data.components)
    ? data.components.map(mapMeta)
    : [];

  const mappedTabs = isTabbed
    ? tabs.map(tab => {
        const tabComponents = Array.isArray(tab.components)
          ? tab.components.map(mapMeta)
          : [];
        const mappedTab = {
          title: tab.title,
          components: tabComponents,
        };

        return {
          ...mappedTab,
          hasUnavailaleComponent: routeHasUnavailableComponent(mappedTab),
        };
      })
    : undefined;

  const routeForAvailability = isTabbed
    ? { tabs: mappedTabs }
    : { components };

  return {
    key,
    title: data.title,
    type: data.type,
    hidden: data.hidden,
    parentPageKey: data.parentPageKey,
    order: data.order,
    isHomepage: data.isHomepage,
    components,
    tabs: mappedTabs,
    hasUnavailaleComponent: routeHasUnavailableComponent(
      routeForAvailability,
      addons,
      specialAddonWithoutSubscription,
      isAppsumoUser,
    ),
  };
};

// Avoid rebuilding the sidebar page list on unrelated Redux updates.
const createMappedChildRoutesSelector = () => {
  let prevLayoutData;
  let prevAlwaysData;
  let prevPlanKey;
  let prevAddonsKey;
  let prevSpecialAddonsKey;
  let prevIsAppsumoUser;
  let prevResult = [];

  return ({
    layoutData,
    alwaysData,
    planKey,
    addons,
    specialAddonWithoutSubscription,
    isAppsumoUser,
    routeHasUnavailableComponent,
  }) => {
    // Object.keys()/filter() create new arrays each call — compare stable keys.
    const addonsKey = addons.join(',');
    const specialAddonsKey = specialAddonWithoutSubscription
      .map(item => item.component)
      .join(',');

    if (
      layoutData === prevLayoutData &&
      alwaysData === prevAlwaysData &&
      planKey === prevPlanKey &&
      addonsKey === prevAddonsKey &&
      specialAddonsKey === prevSpecialAddonsKey &&
      isAppsumoUser === prevIsAppsumoUser
    ) {
      return prevResult;
    }

    prevLayoutData = layoutData;
    prevAlwaysData = alwaysData;
    prevPlanKey = planKey;
    prevAddonsKey = addonsKey;
    prevSpecialAddonsKey = specialAddonsKey;
    prevIsAppsumoUser = isAppsumoUser;

    prevResult = layoutData.childRoutes
      ? layoutData.childRoutes.map(key =>
          mapSidebarRoute(
            key,
            alwaysData,
            routeHasUnavailableComponent,
            addons,
            specialAddonWithoutSubscription,
            isAppsumoUser,
          ),
        )
      : [];

    return prevResult;
  };
};

const selectMappedChildRoutes = createMappedChildRoutesSelector();

const mapStateToProps = (state, ownProps) => {
  const layoutData =
    state.editor.data.layoutData[ownProps.match.params.appname];
  const alwaysData = state.editor.data.alwaysData;
  const diys = state.app.addons.diyComponents || {};
  const addons = Object.keys(diys);
  const specialAddons = state.editor.build.create.createComponent.specialAddons;
  const specialAddonWithoutSubscription = specialAddons.filter(item => {
    return (
      !item.hasActiveSubscription &&
      item.hasFreeTrial &&
      Date.now() > item.expiryDate
    );
  });

  const isAppsumoUser = state.account.organisation.appsumo_id || false;

  if (!layoutData) {
    return { ...ownProps };
  }

  const freeTrialPlan = state.account.organisation.trial_expired
    ? null
    : 'accelerate';

  const subscriptions = state.account.subscriptions.items || [];
  const subscription = subscriptions[ownProps.match.params.appname];
  const plan = subscription && subscription.plan;
  const planKey = freeTrialPlan || plan || 'start';
  const routeHasUnavailableComponent = _routeHasUnavailableComponent(planKey);

  const mappedIndexRoute = {
    title: 'Homepage',
    key: layoutData.index,
    type: alwaysData[layoutData.index] && alwaysData[layoutData.index].type,
  };

  const mappedChildRoutes = selectMappedChildRoutes({
    layoutData,
    alwaysData,
    planKey,
    addons,
    specialAddonWithoutSubscription,
    isAppsumoUser,
    routeHasUnavailableComponent,
  });

  const isAppAdmin =
    (state.account.auth.profile.admin_appname &&
      state.account.auth.profile.admin_appname.length > 0) ||
    false;
  const appcuesVisibilityStatus = state.app.appcues;
  const role = state.account.profile.role;
  const isSubAdminWithMulipleApps =
    (state.account.auth.profile.admin_appnames &&
      Object.keys(state.account.auth.profile.admin_appnames).length > 1) ||
    false;
  return {
    ...ownProps,
    app: state.app,
    index: mappedIndexRoute,
    childRoutes: mappedChildRoutes,
    pagesLoading:
      !state.editor.data.loaded[ownProps.match.params.appname] &&
      mappedChildRoutes.length === 0,
    isPublished: state.editor.data.isPublished[ownProps.match.params.appname],
    isAppAdmin,
    plan,
    appcuesVisibilityStatus,
    role,
    isSubAdminWithMulipleApps,
    account: state.account.profile.account,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  removePage: key =>
    dispatch(
      launchRemoveDialog(ownProps.match.params.appname, { page: key }, 'PAGE'),
    ),
  onRearrangePage: ({ idToMove, moveTo, collection, newCollection }) =>
    dispatch(
      reOrderPages({
        idToMove,
        moveTo,
        collection,
        newCollection,
        appname: ownProps.match.params.appname,
      }),
    ),
  onAddTab: () => dispatch(openCreateTabUI()),
  onAddPage: () => dispatch(openCreatePageUI()),
  onAddIsPublishedListener: () =>
    dispatch(addIsPublishedListener(ownProps.match.params.appname)),
  onRemoveIsPublishedListener: () =>
    dispatch(removeIsPublishedListener(ownProps.match.params.appname)),
  duplicatePage: key => dispatch(launchDuplicatePageDialog(key)),
  getAppAdmins: () => dispatch(getAppAdmins()),
  onAddAdmin: () => dispatch(openNewAdminDialog(ownProps.match.params.appname)),
  openReferalModal: () => {
    dispatch(toggleAccountModal());
  },
  submitAppcuesForms: (type, data) =>
    dispatch(submitAppcuesForms(ownProps.match.params.appname, type, data)),
  submitAppcuesReferFriend: type => dispatch(submitAppcuesReferFriend(type)),
  checkAppcuesVisibleStatus: () => dispatch(checkAppcuesVisibleStatus()),
  onAccountSettingsTouchTap: () => {
    dispatch(toggleUserMenu(false));
    dispatch(toggleAccountModal());
  },
  setActiveAccountTab: value => dispatch(setActiveAccountTab(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BuildMenu);
